/**
 * WebSocket Service for MediaConnect Router
 * Handles real-time communication with backend routing services
 */

import { 
  RoutingUpdateMessage, 
  ConnectionRequestMessage, 
  ConnectionResponseMessage,
  generateRequestId 
} from '@mediaconnect-router/shared'

export interface WebSocketServiceOptions {
  reconnectInterval?: number
  maxReconnectAttempts?: number
  heartbeatInterval?: number
}

export class WebSocketService {
  private ws: WebSocket | null = null
  private url: string
  private options: Required<WebSocketServiceOptions>
  private reconnectAttempts = 0
  private reconnectTimer: NodeJS.Timeout | null = null
  private heartbeatTimer: NodeJS.Timeout | null = null
  private isConnecting = false
  private messageHandlers = new Map<string, (message: any) => void>()
  private pendingRequests = new Map<string, {
    resolve: (response: ConnectionResponseMessage) => void
    reject: (error: Error) => void
    timeout: NodeJS.Timeout
  }>()

  constructor(url: string, options: WebSocketServiceOptions = {}) {
    this.url = url
    this.options = {
      reconnectInterval: options.reconnectInterval ?? 1000,
      maxReconnectAttempts: options.maxReconnectAttempts ?? 10,
      heartbeatInterval: options.heartbeatInterval ?? 30000
    }
  }

  /**
   * Connect to WebSocket server
   */
  async connect(): Promise<void> {
    if (this.isConnecting || (this.ws && this.ws.readyState === WebSocket.OPEN)) {
      return
    }

    this.isConnecting = true

    return new Promise((resolve, reject) => {
      try {
        this.ws = new WebSocket(this.url)

        this.ws.onopen = () => {
          this.isConnecting = false
          this.reconnectAttempts = 0
          this.startHeartbeat()
          if (this.connectionStatusHandler) {
            this.connectionStatusHandler(true)
          }
          resolve()
        }

        this.ws.onmessage = (event) => {
          this.handleMessage(event.data)
        }

        this.ws.onclose = () => {
          this.isConnecting = false
          this.stopHeartbeat()
          if (this.connectionStatusHandler) {
            this.connectionStatusHandler(false)
          }
          this.scheduleReconnect()
        }

        this.ws.onerror = (error) => {
          this.isConnecting = false
          console.error('WebSocket error:', error)
          if (this.reconnectAttempts === 0) {
            reject(new Error('Failed to connect to WebSocket server'))
          }
        }
      } catch (error) {
        this.isConnecting = false
        reject(error)
      }
    })
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }

    this.stopHeartbeat()

    if (this.ws) {
      this.ws.close()
      this.ws = null
    }

    // Reject all pending requests
    this.pendingRequests.forEach(({ reject, timeout }) => {
      clearTimeout(timeout)
      reject(new Error('WebSocket disconnected'))
    })
    this.pendingRequests.clear()
  }

  /**
   * Check if WebSocket is connected
   */
  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN
  }

  /**
   * Send connection request
   */
  async sendConnectionRequest(
    sourceId: string, 
    destinationId: string, 
    action: 'connect' | 'disconnect'
  ): Promise<ConnectionResponseMessage> {
    if (!this.isConnected()) {
      throw new Error('WebSocket not connected')
    }

    const requestId = generateRequestId()
    const message: ConnectionRequestMessage = {
      type: 'connection_request',
      sourceId,
      destinationId,
      action,
      requestId
    }

    return new Promise((resolve, reject) => {
      // Set up timeout for request
      const timeout = setTimeout(() => {
        this.pendingRequests.delete(requestId)
        reject(new Error('Request timeout'))
      }, 5000) // 5 second timeout

      // Store request for response handling
      this.pendingRequests.set(requestId, { resolve, reject, timeout })

      // Send message
      this.ws!.send(JSON.stringify(message))
    })
  }

  /**
   * Register handler for routing updates
   */
  onRoutingUpdate(handler: (message: RoutingUpdateMessage) => void): void {
    this.messageHandlers.set('routing_update', handler)
  }

  /**
   * Register handler for connection status changes
   */
  onConnectionStatusChange(handler: (connected: boolean) => void): void {
    // Store the handler to be called when connection state changes
    this.connectionStatusHandler = handler
  }

  private connectionStatusHandler: ((connected: boolean) => void) | null = null

  /**
   * Handle incoming WebSocket messages
   */
  private handleMessage(data: string): void {
    try {
      const message = JSON.parse(data)

      switch (message.type) {
        case 'routing_update':
          const handler = this.messageHandlers.get('routing_update')
          if (handler) {
            handler(message as RoutingUpdateMessage)
          }
          break

        case 'connection_response':
          const response = message as ConnectionResponseMessage
          const pendingRequest = this.pendingRequests.get(response.requestId)
          if (pendingRequest) {
            clearTimeout(pendingRequest.timeout)
            this.pendingRequests.delete(response.requestId)
            pendingRequest.resolve(response)
          }
          break

        default:
          console.warn('Unknown message type:', message.type)
      }
    } catch (error) {
      console.error('Failed to parse WebSocket message:', error)
    }
  }

  /**
   * Schedule reconnection with exponential backoff
   */
  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.options.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached')
      return
    }

    const delay = Math.min(
      this.options.reconnectInterval * Math.pow(2, this.reconnectAttempts),
      30000 // Max 30 seconds
    )

    this.reconnectTimer = setTimeout(() => {
      this.reconnectAttempts++
      console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.options.maxReconnectAttempts})`)
      this.connect().catch(console.error)
    }, delay)
  }

  /**
   * Start heartbeat to keep connection alive
   */
  private startHeartbeat(): void {
    this.heartbeatTimer = setInterval(() => {
      if (this.isConnected()) {
        this.ws!.send(JSON.stringify({ type: 'ping' }))
      }
    }, this.options.heartbeatInterval)
  }

  /**
   * Stop heartbeat timer
   */
  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
  }
}